import React, { useCallback, useEffect, useState } from 'react'
import { api, formatCurrencyFromCents } from '../../utils/api'
import { postPriceWithFloorConfirm } from '../../utils/consignmentFloor'
import { useReverbOffers } from '../../hooks/useReverbOffers.jsx'
import { timeLeft, deadlineDate } from './ebayRequestFormat.js'
import OffOnStaging from '../OffOnStaging'
import {
  OWNERSHIP_LABEL,
  blockedReason,
  expiryBadge,
  penceToPounds,
  poundsToPence,
  respondErrorMessage
} from './reverbOfferFormat.js'

// Reverb Buyer Offers: what buyers have offered on our Reverb listings, with
// the facts to decide on, and accept / decline / counter. Prism re-checks the
// offer with Reverb before sending. Spec: audiotrader-prism
// specs/specs_20261015_reverb_offers.md
const STATUS_FILTERS = [
  { value: 'awaiting_us', label: 'Waiting for us' },
  { value: 'awaiting_buyer', label: 'Waiting for buyer' },
  { value: 'closed', label: 'Closed' }
]
// Shown under Prism's "Below the seller's floor of £X" (ADR-0038: consignor first).
const FLOOR_CONFIRM = 'Have you agreed this price with the consignor?'

function ReverbOffersTab() {
  const { refresh: refreshBadge } = useReverbOffers()
  const [status, setStatus] = useState('awaiting_us')
  const [offers, setOffers] = useState([])
  const [loading, setLoading] = useState(true)
  const [message, setMessage] = useState(null)
  const [busyId, setBusyId] = useState(null)
  const [counterFor, setCounterFor] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await api.get(`/api/orders/reverb-offers?status=${status}`)
      setOffers(data.offers || [])
    } catch (error) {
      setMessage({ kind: 'error', text: `Could not load Reverb offers: ${error.message}` })
    } finally {
      setLoading(false)
    }
  }, [status])

  useEffect(() => { load() }, [load])

  const respond = useCallback(async (offer, body) => {
    setBusyId(offer.reverb_negotiation_id)
    setMessage(null)
    const url = `/api/orders/reverb-offers/${offer.reverb_negotiation_id}/respond`
    const payload = { ...body, expected_offer_price_cents: offer.offer_price_cents }
    try {
      await postPriceWithFloorConfirm(url, payload, FLOOR_CONFIRM)
      setMessage({ kind: 'success', text: `Sent to Reverb: ${body.action}.` })
      setCounterFor(null)
    } catch (error) {
      setMessage({ kind: 'error', text: respondErrorMessage(error) })
    } finally {
      setBusyId(null)
      await load()
      refreshBadge()
    }
  }, [load, refreshBadge])

  const accept = offer => {
    const total = formatCurrencyFromCents(offer.offer_price_cents + (offer.offer_shipping_cents || 0))
    if (window.confirm(`Accept ${total}? This is binding: Reverb creates the order at once.`)) {
      respond(offer, { action: 'accept' })
    }
  }
  const decline = offer => {
    if (window.confirm('Decline this offer?')) respond(offer, { action: 'decline' })
  }

  return (
    <div className="card" style={{ marginTop: 'var(--space-3)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0 }}>Reverb offers ({offers.length})</h3>
        <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
          {STATUS_FILTERS.map(filter => (
            <button
              key={filter.value}
              className={`btn btn-sm ${status === filter.value ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setStatus(filter.value)}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {message && (
        <p role="alert" className={`badge ${message.kind === 'error' ? 'badge-error' : 'badge-success'}`} style={{ marginTop: 'var(--space-2)' }}>
          {message.text}
        </p>
      )}

      {loading ? (
        <p className="text-muted" style={{ marginTop: 'var(--space-2)' }}>Loading...</p>
      ) : offers.length === 0 ? (
        <p className="text-muted" style={{ marginTop: 'var(--space-2)' }}>No offers here.</p>
      ) : (
        <div style={{ overflowX: 'auto', marginTop: 'var(--space-2)' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Expires</th><th>Item</th><th>Type</th><th>Age</th><th>Cost</th>
                <th>Price (Reverb / D2C)</th><th>Offer</th><th>Shipping (buyer / ours est.)</th>
                <th>Country</th><th>Margin</th><th></th>
              </tr>
            </thead>
            <tbody>
              {offers.map(offer => (
                <OfferRow
                  key={offer.reverb_negotiation_id}
                  offer={offer}
                  canRespond={status === 'awaiting_us'}
                  busy={busyId === offer.reverb_negotiation_id}
                  counterOpen={counterFor === offer.reverb_negotiation_id}
                  onAccept={() => accept(offer)}
                  onDecline={() => decline(offer)}
                  onCounterOpen={() => setCounterFor(offer.reverb_negotiation_id)}
                  onCounterCancel={() => setCounterFor(null)}
                  onCounter={(price, shipping) => respond(offer, {
                    action: 'counter', counter_price_cents: price, counter_shipping_cents: shipping
                  })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function OfferRow({ offer, canRespond, busy, counterOpen, onAccept, onDecline, onCounterOpen, onCounterCancel, onCounter }) {
  const item = offer.item
  const blocked = blockedReason(offer)
  const money = cents => formatCurrencyFromCents(cents, offer.currency)

  return (
    <>
      <tr>
        <td style={{ whiteSpace: 'nowrap' }}>
          <div>{deadlineDate(offer.expires_at) || '-'}</div>
          <span className={`badge ${expiryBadge(offer.expires_at)}`}>{timeLeft(offer.expires_at)}</span>
        </td>
        <td>
          {offer.reverb_listing_id
            ? <a href={`https://reverb.com/item/${offer.reverb_listing_id}`} target="_blank" rel="noreferrer">{offer.title || `Reverb listing ${offer.reverb_listing_id}`}</a>
            : (offer.title || 'Unknown item')}
          {item && !item.in_stock && <div><span className="badge badge-error">Sold elsewhere</span></div>}
        </td>
        <td>{item ? OWNERSHIP_LABEL[item.ownership] || item.ownership : '-'}</td>
        <td>{offer.age_days == null ? '-' : `${offer.age_days} days`}</td>
        <td>{item?.ownership === 'consignment' ? <span className="text-muted">n/a</span> : money(item?.cost_cents)}</td>
        <td style={{ whiteSpace: 'nowrap' }}>
          {money(item?.reverb_price_cents)}
          <div className="text-muted">D2C {money(item?.d2c_price_cents)}</div>
        </td>
        <td style={{ whiteSpace: 'nowrap' }}>
          <strong>{money(offer.offer_price_cents)}</strong>
          {offer.pct_off_reverb != null && <div className="text-muted">-{offer.pct_off_reverb.toFixed(1)}%</div>}
          {offer.below_consignment_floor && <div><span className="badge badge-warning" title={`Seller's floor ${money(item?.consignment_floor_cents)}`}>Below floor</span></div>}
        </td>
        <td style={{ whiteSpace: 'nowrap' }}>
          {money(offer.offer_shipping_cents)}
          <div className="text-muted">{offer.our_shipping_cents == null ? 'no rate' : `${money(offer.our_shipping_cents)} est.`}</div>
        </td>
        <td>{offer.buyer_country || '-'}</td>
        <td style={{ whiteSpace: 'nowrap', color: offer.margin_cents < 0 ? 'var(--color-error)' : undefined, fontWeight: 600 }}>
          {offer.margin_cents == null ? <span className="text-muted">-</span> : money(offer.margin_cents)}
        </td>
        <td style={{ whiteSpace: 'nowrap' }}>
          {canRespond && (
            <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
              <OffOnStaging feature="reverb_write">
                <button className="btn btn-sm btn-primary" onClick={onAccept} disabled={busy || Boolean(blocked)} title={blocked || undefined}>Accept</button>
                <button className="btn btn-sm btn-secondary" onClick={onCounterOpen} disabled={busy || Boolean(blocked)} title={blocked || undefined}>Counter</button>
                <button className="btn btn-sm btn-secondary" onClick={onDecline} disabled={busy}>Decline</button>
              </OffOnStaging>
            </div>
          )}
        </td>
      </tr>
      {counterOpen && (
        <tr>
          <td colSpan={11}>
            <CounterForm offer={offer} busy={busy} onSubmit={onCounter} onCancel={onCounterCancel} />
          </td>
        </tr>
      )}
    </>
  )
}

function CounterForm({ offer, busy, onSubmit, onCancel }) {
  const [price, setPrice] = useState('')
  const [shipping, setShipping] = useState(penceToPounds(offer.offer_shipping_cents ?? 0))
  const pricePence = poundsToPence(price)
  const shippingPence = poundsToPence(shipping)
  const valid = pricePence != null && shippingPence != null

  return (
    <form
      style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'end', flexWrap: 'wrap' }}
      onSubmit={event => { event.preventDefault(); if (valid) onSubmit(pricePence, shippingPence) }}
    >
      <label>
        Counter price (£)
        <input className="form-input" inputMode="decimal" value={price} onChange={e => setPrice(e.target.value)} autoFocus />
      </label>
      <label>
        Shipping (£)
        <input className="form-input" inputMode="decimal" value={shipping} onChange={e => setShipping(e.target.value)} />
      </label>
      <button className="btn btn-sm btn-primary" type="submit" disabled={busy || !valid}>Send counter</button>
      <button className="btn btn-sm btn-secondary" type="button" onClick={onCancel}>Cancel</button>
    </form>
  )
}

export default ReverbOffersTab
